import { z } from 'zod'

import { api } from '#admin/lib/api-client.ts'

/**
 * Platform staff looking after logins — every user on the platform, across every edition.
 *
 * Searched and paged **on the server** (`nextCursor`), unlike every other list in this console:
 * this is the one table that grows with every delegate of every event.
 */

export const userSummarySchema = z.object({
  id: z.uuid(),
  email: z.string(),
  name: z.string(),
  createdAt: z.string(),
  banned: z.boolean(),
  isStaff: z.boolean(),
  memberships: z.number(),
})

export type UserSummary = z.infer<typeof userSummarySchema>

const userPageSchema = z.object({
  items: z.array(userSummarySchema),
  nextCursor: z.string().nullable(),
})

export type UserPage = z.infer<typeof userPageSchema>

export const userDetailSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  name: z.string(),
  emailVerified: z.boolean(),
  createdAt: z.string(),
  banned: z.boolean(),
  banReason: z.string().nullable(),
  banExpires: z.string().nullable(),
  twoFactorEnabled: z.boolean(),
  signInMethods: z.array(z.string()),
  staffScopes: z.array(z.string()).nullable(),
  memberships: z.array(
    z.object({
      tenantId: z.uuid(),
      tenantSlug: z.string(),
      tenantName: z.string(),
      role: z.string(),
      joinedAt: z.string(),
    }),
  ),
  sessions: z.array(
    z.object({
      id: z.uuid(),
      createdAt: z.string(),
      expiresAt: z.string(),
      ipAddress: z.string().nullable(),
      userAgent: z.string().nullable(),
      impersonatedBy: z.uuid().nullable(),
    }),
  ),
  history: z.array(
    z.object({
      id: z.uuid(),
      action: z.string(),
      actorEmail: z.string().nullable(),
      reason: z.string().nullable(),
      createdAt: z.string(),
      detail: z.record(z.string(), z.unknown()),
    }),
  ),
})

export type UserDetail = z.infer<typeof userDetailSchema>

const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {})

export function fetchUsers(
  query: { q: string; cursor?: string | undefined },
  signal?: AbortSignal,
): Promise<UserPage> {
  const params = new URLSearchParams({ limit: '50' })
  if (query.q !== '') params.set('q', query.q)
  if (query.cursor !== undefined) params.set('cursor', query.cursor)
  return api.get(`/v1/platform/users?${params.toString()}`, {
    schema: userPageSchema,
    ...withSignal(signal),
  })
}

export function fetchUser(userId: string, signal?: AbortSignal): Promise<UserDetail> {
  return api.get(`/v1/platform/users/${userId}`, {
    schema: userDetailSchema,
    ...withSignal(signal),
  })
}

const tenantRolesSchema = z.object({
  items: z.array(z.object({ key: z.string(), name: z.string() })),
})

export function fetchTenantRoles(
  tenantId: string,
  signal?: AbortSignal,
): Promise<z.infer<typeof tenantRolesSchema>> {
  return api.get(`/v1/platform/tenants/${tenantId}/roles`, {
    schema: tenantRolesSchema,
    ...withSignal(signal),
  })
}

const ok = z.object({ ok: z.literal(true) })

export function banUser(userId: string, reason: string, expiresAt?: string) {
  return api.post(`/v1/platform/users/${userId}/ban`, {
    schema: ok,
    body: { reason, ...(expiresAt === undefined ? {} : { expiresAt }) },
  })
}

export function unbanUser(userId: string, reason: string) {
  return api.post(`/v1/platform/users/${userId}/unban`, { schema: ok, body: { reason } })
}

export function revokeSessions(userId: string, reason: string) {
  return api.post(`/v1/platform/users/${userId}/revoke-sessions`, {
    schema: z.object({ revoked: z.number() }),
    body: { reason },
  })
}

export function sendSignInLink(userId: string) {
  return api.post(`/v1/platform/users/${userId}/sign-in-link`, { schema: ok })
}

export function setMembership(userId: string, tenantId: string, role: string, reason: string) {
  return api.put(`/v1/platform/users/${userId}/memberships/${tenantId}`, {
    schema: ok,
    body: { role, reason },
  })
}

export function removeMembership(userId: string, tenantId: string, reason: string) {
  return api.delete(`/v1/platform/users/${userId}/memberships/${tenantId}`, {
    schema: ok,
    body: { reason },
  })
}

/**
 * Sign in as this user (`/api/auth/platform-impersonation/start`).
 *
 * Better Auth's endpoint rather than a `/v1/platform` route, because it has to swap the session
 * cookie — which only the auth handler can do. The session it opens lasts thirty minutes.
 */
export function startImpersonation(input: { userId: string; reason: string; tenantId?: string }) {
  return api.post('/api/auth/platform-impersonation/start', {
    schema: z.object({ userId: z.string(), expiresAt: z.string() }),
    body: input,
  })
}

export function stopImpersonation() {
  return api.post('/api/auth/platform-impersonation/stop', {
    schema: z.object({ restored: z.boolean() }),
    body: {},
  })
}

/** What an entry in a user's staff history says, in words. */
export function actionLabel(action: string): string {
  const labels: Record<string, string> = {
    'platform.user_banned': 'Banned',
    'platform.user_unbanned': 'Ban lifted',
    'platform.sessions_revoked': 'Signed out everywhere',
    'platform.sign_in_link_sent': 'Sign-in link sent',
    'platform.membership_set': 'Membership set',
    'platform.membership_removed': 'Removed from an edition',
    'platform.impersonation_started': 'Impersonation started',
    'platform.impersonation_stopped': 'Impersonation ended',
    'platform.impersonation_refused': 'Impersonation refused',
  }
  return labels[action] ?? action.replace(/^platform\./, '').replaceAll('_', ' ')
}

/** The minimum the server accepts for a reason; the dialogs count toward it. */
export const REASON_MIN = 10
